/**
 * FinanceApprovals — the pending invoice-approval queue from /v1/approvals
 * (status=pending), selected CLIENT-SIDE by payload shape via the shared
 * asExtractedInvoicePayload guard (no server-side skill filter exists, and the
 * shape is what we render from), with a detail drawer carrying the real HITL
 * decide gate (POST /v1/approvals/{id}/decision via decideApproval).
 *
 * NOT the Invoices page's approve. That approve is a status flip on an
 * existing finance_invoices row. THIS approve is the enrichment commit: it
 * CREATES the finance_invoices row, records the reconciliation disposition,
 * and — only when disposition is ok_to_pay — queues a REAL draft bill in
 * Xero. Rejecting FAILS the run (approval_rejected). The footer copy states
 * exactly that, per disposition.
 *
 * decideApproval is NEVER called here with an editedPayload — a non-empty edit
 * replaces the WHOLE payload on a terminal enrichment gate (see
 * features/review/extractedInvoice.ts). Rejection carries a required
 * justification, which the backend records as the reason on the
 * approval.rejected run_event.
 *
 * All colours flow from theme/tokens.ts — no raw hex here.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Check } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { usePolledAsync, fetchApprovalsEveryStatus, decideApproval } from '../../api'
import type { DashApproval } from '../../types'
import { ApiError } from '../../../../api/client'
import { asExtractedInvoicePayload } from '../../../../features/review/extractedInvoice'
import type { ExtractedInvoice } from '../../../../features/review/extractedInvoice'
import { LineItemsCard } from './LineItemsCard'
import type { ReconciliationTotals } from './LineItemsCard'
import { money } from '../../../../lib/money'
import { toast } from 'sonner'
import { attention, danger, edge, ink, neutral, overlay, statusTicket, surface } from '../../../../theme/tokens'

// Disposition → quiet dot + label, the InvoiceStatus idiom. needs_review rides
// the attention amber (quiet urgency), ok_to_pay the resolved sage pair,
// duplicate the danger family. Unknown/absent falls back to the neutral dot.
const DISPOSITION_CHIP: Record<string, { dot: string; label: string; text: string }> = {
  needs_review: { dot: attention.indicator, label: attention.wordmark, text: 'Needs review' },
  ok_to_pay:    { dot: statusTicket.resolved.dot, label: statusTicket.resolved.label, text: 'OK to pay' },
  duplicate:    { dot: danger.DEFAULT, label: danger.text, text: 'Duplicate' },
}

// Disposition → reconciliation strip (the drawer's decision-support banner).
const STRIP: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  needs_review: { bg: attention['banner-bg'], border: attention['banner-border'], text: attention['banner-text'], dot: attention.indicator },
  ok_to_pay:    { bg: surface.panel, border: edge.panel, text: statusTicket.resolved.label, dot: statusTicket.resolved.dot },
  duplicate:    { bg: danger.bg, border: danger.bg, text: danger.text, dot: danger.DEFAULT },
}
const STRIP_FALLBACK = { bg: surface.panel, border: edge.panel, text: ink.secondary, dot: neutral.dot }

// The consequence line — LOAD-BEARING copy. Only ok_to_pay queues a Xero
// draft on approve; every other disposition (and a missing reconciliation)
// records the invoice and queues nothing. Rejection always fails the run.
function consequenceLine(disposition: string | null): string {
  return disposition === 'ok_to_pay'
    ? 'Approving records the invoice and queues a draft bill in Xero. Rejecting fails the run.'
    : 'Approving records the invoice. No Xero draft will be created. Rejecting fails the run.'
}

function fmtDate(d?: string): string {
  return d ? new Date(d).toLocaleDateString('en-GB') : '—'
}

function fmtDateTime(d?: string | null): string {
  return d
    ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—'
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

// A pending invoice gate: the approval row plus its parsed payload pieces.
interface PendingInvoiceApproval {
  approval:    DashApproval
  invoice:     ExtractedInvoice
  disposition: string | null
  report:      string | null
  totals:      ReconciliationTotals | null
}

// Reads the reconciliation block the orchestrator attaches beside
// __finance_invoice: the disposition drives the chip + consequence copy,
// payload.report is its ready-made one-line summary, and totals is the
// engine's derived subtotal/VAT/total breakdown (absent on gates reconciled
// before the engine exposed it — permanent, there is no backfill).
function reconciliationOf(payload?: Record<string, unknown> | null): { disposition: string | null; report: string | null; totals: ReconciliationTotals | null } {
  if (!payload || typeof payload !== 'object') return { disposition: null, report: null, totals: null }
  const rec = payload.__reconciliation
  const disposition =
    rec && typeof rec === 'object' && typeof (rec as Record<string, unknown>).disposition === 'string'
      ? ((rec as Record<string, unknown>).disposition as string)
      : null
  const report = typeof payload.report === 'string' ? payload.report : null
  return { disposition, report, totals: totalsOf(rec) }
}

// Narrows __reconciliation.totals (snake_case on the wire) to the shared
// ReconciliationTotals shape; any missing or non-numeric field means the
// breakdown is absent, never partially rendered.
function totalsOf(rec: unknown): ReconciliationTotals | null {
  if (!rec || typeof rec !== 'object') return null
  const t = (rec as Record<string, unknown>).totals
  if (!t || typeof t !== 'object') return null
  const o = t as Record<string, unknown>
  return typeof o.subtotal === 'number' && typeof o.vat_rate === 'number' && typeof o.vat_amount === 'number' && typeof o.total === 'number'
    ? { subtotal: o.subtotal, vatRate: o.vat_rate, vatAmount: o.vat_amount, total: o.total }
    : null
}

function DispositionChip({ disposition }: { disposition: string | null }) {
  const cfg = (disposition ? DISPOSITION_CHIP[disposition] : undefined)
    ?? { dot: neutral.dot, label: ink.secondary, text: disposition ? disposition.replace(/_/g, ' ') : 'Not reconciled' }
  return (
    <span className="inline-flex items-center gap-2 text-12 font-medium whitespace-nowrap" style={{ color: cfg.label }}>
      <span className="rounded-full flex-shrink-0" style={{ width: 6, height: 6, background: cfg.dot }} />
      {cfg.text}
    </span>
  )
}

// ── Detail drawer ───────────────────────────────────────────────────────────────

function ApprovalDetailDrawer({ item, canDecide, onClose, onApprove, onReject, busy }: {
  item: PendingInvoiceApproval
  canDecide: boolean
  onClose: () => void
  onApprove: () => void
  onReject: (reason: string) => void
  busy: 'approved' | 'rejected' | null
}) {
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const { invoice, approval, disposition, report, totals } = item
  const strip = (disposition ? STRIP[disposition] : undefined) ?? STRIP_FALLBACK
  const items = invoice.lineItems ?? []

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(760px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header — overline (invoice ref + disposition), then vendor — amount */}
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="k-eyebrow uppercase">{invoice.invoiceNumber || 'No invoice #'}</span>
              <DispositionChip disposition={disposition} />
            </div>
            <div className="text-detail-title font-semibold text-ink-primary truncate">{invoice.vendorName} — {money(invoice.amount ?? 0, invoice.currency)}</div>
            <div className="text-12 text-ink-muted mt-1">
              Requested {fmtDateTime(approval.requestedAt)}{approval.expiresAt && ` · expires ${fmtDateTime(approval.expiresAt)}`}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          {/* Extracted fields — real only: Amount + Due date */}
          <div className="rounded-tile bg-surface-panel border border-edge-panel px-5 py-4 grid grid-cols-2 gap-4">
            <div>
              <div className="k-eyebrow uppercase mb-1">Amount</div>
              <div className="text-14 font-semibold text-ink-primary tabular-nums">{money(invoice.amount ?? 0, invoice.currency)}</div>
            </div>
            <div>
              <div className="k-eyebrow uppercase mb-1">Due date</div>
              <div className="text-14 font-medium text-ink-primary tabular-nums">{fmtDate(invoice.dueDate)}</div>
            </div>
          </div>

          {/* Reconciliation — the decision-support strip Mission Control omits */}
          <div>
            <div className="k-eyebrow uppercase mb-1">Reconciliation</div>
            <div className="flex items-start gap-2 px-3 py-2 rounded-tile text-12 leading-relaxed"
              style={{ background: strip.bg, border: `1px solid ${strip.border}`, color: strip.text }}>
              <span className="rounded-full flex-shrink-0 mt-[5px]" style={{ width: 6, height: 6, background: strip.dot }} />
              {report ?? 'This invoice has not been reconciled.'}
            </div>
          </div>

          {/* AI summary */}
          {invoice.aiSummary && (
            <div>
              <div className="k-eyebrow uppercase mb-1">AI extraction summary</div>
              <p className="text-13 text-ink-body leading-relaxed">{invoice.aiSummary}</p>
            </div>
          )}

          {/* Line items — the shared LineItemsCard (one totals renderer for
              this drawer and the Invoices drawer, so they cannot disagree).
              With the engine's breakdown the rows add up: Subtotal / VAT /
              Total, all backend figures. Without it (gates reconciled before
              the engine exposed totals) the line sum is labelled as the line
              sum — never as the invoice total — above the stated gross. */}
          <LineItemsCard items={items} currency={invoice.currency} total={invoice.amount ?? 0} totals={totals} />
        </div>

        {/* Footer — decide actions (role-gated) + the disposition-honest consequence */}
        <div className="px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          {canDecide ? (
            <>
              {rejectOpen && (
                <div className="mb-3">
                  <label htmlFor="fa-reject-reason" className="k-eyebrow uppercase block mb-1">
                    Reason for rejection
                    <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-muted">(required — recorded on the audit trail)</span>
                  </label>
                  <textarea
                    id="fa-reject-reason"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    autoFocus
                    placeholder="Why is this invoice being rejected?"
                    className="w-full px-3 py-2 text-13 leading-relaxed bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent resize-y"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <button onClick={() => (rejectOpen ? onReject(rejectReason.trim()) : setRejectOpen(true))} disabled={busy !== null || (rejectOpen && rejectReason.trim() === '')}
                  className="k-btn-secondary flex-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  {busy === 'rejected' ? 'Rejecting…' : rejectOpen ? 'Confirm reject' : 'Reject'}
                </button>
                <button onClick={onApprove} disabled={busy !== null}
                  className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                  <Check size={14} /> {busy === 'approved' ? 'Approving…' : 'Approve invoice'}
                </button>
              </div>
              <div className="text-11 text-ink-muted mt-2">{consequenceLine(disposition)}</div>
            </>
          ) : (
            <div className="text-12 text-ink-muted">You don't have permission to decide approvals. Ask your workspace owner.</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

// The filter chips (FIN-1) — the FinanceInvoices chip contract verbatim.
// 'all' is a CLIENT-side union, not a server value: the endpoint rewrites an
// empty ?status= to 'pending', so "every status" only exists as the fan-out
// (fetchApprovalsEveryStatus). It therefore also carries `edited` rows, which
// have no chip of their own — reachable under All rather than invisible.
const FILTERS = ['pending', 'approved', 'rejected', 'expired', 'all'] as const
type ApprovalFilter = (typeof FILTERS)[number]
const FILTER_LABEL: Record<ApprovalFilter, string> = {
  pending: 'Pending', approved: 'Approved', rejected: 'Rejected', expired: 'Expired', all: 'All',
}
// What each chip says when its own list is empty — the pending case is the
// one that used to read as a broken page.
const EMPTY_COPY: Record<ApprovalFilter, { message: string; hint: string }> = {
  pending: {
    message: 'No approvals awaiting a decision',
    hint: 'Invoice approvals appear here when an emailed or uploaded invoice needs a human decision. Decided ones are still on the other tabs.',
  },
  approved: { message: 'No approved invoice approvals', hint: 'Approvals you accept appear here.' },
  rejected: { message: 'No rejected invoice approvals', hint: 'Approvals you decline appear here.' },
  expired: { message: 'No expired invoice approvals', hint: 'An approval expires when nobody decides it in time.' },
  all: { message: 'No invoice approvals yet', hint: 'Invoice approvals appear here when an emailed or uploaded invoice needs a human decision.' },
}

export default function FinanceApprovals({ canDecide }: { canDecide: boolean }) {
  // Every status, not just pending (FIN-1): decided approvals were previously
  // unreachable from this page — the server defaults ?status= to 'pending'.
  const { data, loading, error } = usePolledAsync(fetchApprovalsEveryStatus, 10_000, [])
  const [approvals, setApprovals] = useState<DashApproval[]>([])
  const [detail, setDetail] = useState<PendingInvoiceApproval | null>(null)
  const [busy, setBusy] = useState<'approved' | 'rejected' | null>(null)
  const [filter, setFilter] = useState<ApprovalFilter>('pending')

  useEffect(() => { if (data) setApprovals(data) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  // Invoice gates only, selected by payload shape. Row text comes from the
  // parsed payload — DashApproval.summary has no meaningful value for these.
  const allItems: PendingInvoiceApproval[] = approvals.flatMap(a => {
    const invoice = asExtractedInvoicePayload(a.payload)
    if (!invoice) return []
    const { disposition, report, totals } = reconciliationOf(a.payload)
    return [{ approval: a, invoice, disposition, report, totals }]
  })
  // Counts are computed AFTER the invoice-shape filter, so a chip can never
  // promise rows this page does not render (a draft-reply approval is a real
  // approval but not an invoice gate, and belongs to the HR/outbound arc).
  const itemsFor = (f: ApprovalFilter) =>
    f === 'all' ? allItems : allItems.filter(i => i.approval.status === f)
  const countFor = (f: ApprovalFilter) => itemsFor(f).length
  const items = itemsFor(filter)

  async function decide(item: PendingInvoiceApproval, status: 'approved' | 'rejected', reason?: string) {
    setBusy(status)
    try {
      await decideApproval(item.approval.id, status, undefined, reason)
      // Re-status in place rather than dropping the row: the chips recount
      // immediately and the decided approval is visible on its new tab,
      // instead of vanishing until the next 10s poll.
      setApprovals(prev => prev.map(a => (a.id === item.approval.id ? { ...a, status } : a)))
      setDetail(null)
      toast.success(status === 'approved'
        ? 'Invoice approved — recorded in Invoices'
        : 'Approval rejected — the run was failed')
    } catch (e) {
      const forbidden = e instanceof ApiError && e.status === 403
      toast.error(forbidden
        ? "You don't have permission to decide approvals. Ask your workspace owner."
        : e instanceof Error ? e.message : 'Action failed')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4 mx-auto" style={{ maxWidth: 1040, minWidth: 920 }}>
      <SectionTitle
        title="Approvals"
        subtitle={`Finance · ${countFor('pending')} pending invoice ${plural(countFor('pending'), 'approval', 'approvals')}`}
      />

      {/* Status chips (real counts) — the FinanceInvoices pattern verbatim. */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(f => {
          const active = filter === f
          return (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors ${active ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
              {FILTER_LABEL[f]} <span className={active ? 'opacity-70' : 'text-ink-muted'}>{countFor(f)}</span>
            </button>
          )
        })}
      </div>

      <div className="k-card">
        {items.length === 0 ? (
          <EmptyState message={EMPTY_COPY[filter].message} hint={EMPTY_COPY[filter].hint} />
        ) : (
          <div>
            {/* Column header */}
            <div className="finance-thead flex items-center gap-4 px-5 py-2 border-b border-edge-row">
              <span className="flex-1 min-w-0">Vendor</span>
              <span className="w-32 flex-shrink-0">Invoice</span>
              <span className="w-24 flex-shrink-0 text-right">Amount</span>
              <span className="w-[120px] flex-shrink-0">Disposition</span>
              <span className="w-[130px] flex-shrink-0 text-right">Requested</span>
              <span className="w-[130px] flex-shrink-0 text-right">Expires</span>
            </div>
            {/* Rows */}
            {items.map(item => (
              <div key={item.approval.id} onClick={() => setDetail(item)}
                className="flex items-center gap-4 px-5 py-3 border-b border-edge-row-faint last:border-b-0 cursor-pointer hover:bg-surface-row-hover transition-colors">
                <span className="flex-1 min-w-0 text-14 font-medium text-ink-primary truncate">{item.invoice.vendorName}</span>
                <span className="w-32 flex-shrink-0 text-12 font-medium text-ink-secondary tabular-nums truncate">{item.invoice.invoiceNumber || '—'}</span>
                <span className="w-24 flex-shrink-0 text-right text-14 font-medium text-ink-primary tabular-nums">{money(item.invoice.amount ?? 0, item.invoice.currency)}</span>
                <span className="w-[120px] flex-shrink-0"><DispositionChip disposition={item.disposition} /></span>
                <span className="w-[130px] flex-shrink-0 text-right text-12 text-ink-secondary tabular-nums">{fmtDateTime(item.approval.requestedAt)}</span>
                <span className="w-[130px] flex-shrink-0 text-right text-12 text-ink-muted tabular-nums">{fmtDateTime(item.approval.expiresAt)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {detail && (
        <ApprovalDetailDrawer
          item={detail}
          canDecide={canDecide}
          onClose={() => setDetail(null)}
          onApprove={() => decide(detail, 'approved')}
          onReject={(reason) => decide(detail, 'rejected', reason)}
          busy={busy}
        />
      )}
    </div>
  )
}
